import Mission from "@/models/Mission";
import Parcel from "@/models/Parcel";
import Dashboard from "@/models/Dashboard";
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { dashboardApi, projectApi } from "@/api/projectClient";

const MissionRepo = useRepo(Mission);
const ParcelRepo = useRepo(Parcel);
const DashboardRepo = useRepo(Dashboard);

export const useMissionStore = defineStore("mission", {
  state: () => ({
    currentMissionUuid: null,
  }),

  actions: {
    async retrieveMission(uuid) {
      const response = await projectApi.retrieve({ uuid });
      this.setCurrentMission(uuid);
      const data = response;
      this.insertProjectData(data);
      MissionRepo.insert(data);
    },

    async updateMission(missionData) {
      const excludeFields = ["id", "$id", "parcel"];
      if (missionData.chronorapso?.includes("/tmp_")) {
        excludeFields.push("agencyCode", "agencyName");
      }
      const cleanedMissionData = { ...missionData };
      for (const field of excludeFields) {
        delete cleanedMissionData[field];
      }
      const response = await projectApi.partialUpdate({
        uuid: cleanedMissionData.uuid,
        patchedProjectRequest: cleanedMissionData,
      });
      const data = response;
      MissionRepo.insert(data);
      this.insertProjectData(data);
      return data;
    },

    async retrieveMissionDashboard(uuid) {
      const dashboard = await dashboardApi.retrieve({ uuid });
      DashboardRepo.save(dashboard);
    },

    async updateDashboard(dashboardData) {
      const dashboard = await dashboardApi.update({
        uuid: dashboardData.uuid,
        dashboardRequest: dashboardData,
      });
      DashboardRepo.insert(dashboard);
    },

    async resetMission() {
      const mission = this.currentMissionUuid;
      const resp = await projectApi.reset({ uuid: mission });
      return resp;
    },

    async copyOfferDataToMission({ offerId, projectId }) {
      const response = await projectApi.importOffer({
        uuid: projectId,
        importOfferRequestRequest: { offerId },
      });
      const data = response;
      this.insertProjectData(data);
      MissionRepo.insert(data);
      return data;
    },

    async fetchAndInsertNonTechnicalSummary({ uuid }) {
      const data = await projectApi.nonTechnicalResume({ uuid });
      this.insertProjectData(data);
      MissionRepo.insert(data);
      return data;
    },

    async mappingsDashboardElementaryMissions(oldElementaryMissions) {
      const currentDashboard = this.currentDashboard;
      const addedElementaryMissions = this.currentMission.elementaryMissions.filter(
        (item) => !oldElementaryMissions.includes(item),
      );
      const removedElementaryMissions = oldElementaryMissions.filter(
        (item) => !this.currentMission.elementaryMissions.includes(item),
      );
      if (addedElementaryMissions.length === 0 && removedElementaryMissions.length === 0) {
        return;
      }
      const elementaryMissionsMappings = {
        A200: ["isGroundInvestigated"],
        A210: ["isUndergroundWaterInvestigated"],
        A220: ["isSuperficialWaterInvestigated", "isSedimentInvestigated"],
        A230: ["isGasInvestigated"],
        A240: ["isAmbientAirInvestigated"],
        A250: ["isTapWaterInvestigated", "isVegetalInvestigated"],
        A260: ["isLandToExcavateInvestigated"],
      };
      const setDashboardFlags = (elementaryMissions, value) => {
        elementaryMissions.forEach((elementaryMission) => {
          (elementaryMissionsMappings[elementaryMission] || []).forEach((key) => {
            currentDashboard[key] = value;
          });
        });
      };
      setDashboardFlags(removedElementaryMissions, false);
      setDashboardFlags(addedElementaryMissions, true);
      try {
        await this.updateDashboard(currentDashboard);
      } catch (error) {
        console.error("Error updating the dashboard:", error);
      }
    },

    async archiveOrUnarchiveMission({ chronorapso, isArchived }) {
      await projectApi.archiveOrUnarchive({
        archiveOrUnarchiveRequestRequest: { chronorapso, isArchived },
      });
    },

    setCurrentMission(missionUUid) {
      this.currentMissionUuid = missionUUid;
    },

    insertProjectData(project) {
      const { parcel = [] } = project;
      ParcelRepo.insert(parcel);
    },
    async checkSourceOption(projectId) {
      const response = await projectApi.checkSourceOption({ uuid: projectId });
      return response.sourceOption;
    },
    async getInvestigatedEnvironments(projectId) {
      const response = await projectApi.investigatedEnvironments({
        uuid: projectId,
      });
      return response.investigatedEnvironments;
    },
  },

  getters: {
    currentMission: (state) => {
      return (
        MissionRepo.where("uuid", state.currentMissionUuid).with("manager").with("case").first() ||
        {}
      );
    },

    currentMissionGedId: (state) => {
      const mission =
        MissionRepo.where("uuid", state.currentMissionUuid).with("manager").with("case").first() ||
        {};

      return mission.originId;
    },

    currentDashboard: (state) => {
      return DashboardRepo.where("project", state.currentMissionUuid).first() || {};
    },

    isTechnicalOffer: (state) => {
      const mission = MissionRepo.where("uuid", state.currentMissionUuid).first();
      return mission?.chronorapso.includes("/tmp_");
    },
  },
});
